import { isDevelopment } from './utils/env';

// Firebase configuration for the app
// Values are loaded from environment variables
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: 'starstruck.space',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Validate that all required environment variables are present
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
];

requiredEnvVars.forEach(envVar => {
  if (!import.meta.env[envVar]) {
    console.error(`Missing required environment variable: ${envVar}`);
  }
});

// Admin settings
export const adminSettings = {
  // Password to access the analysis page - change this for security!
  password: "starstruck-admin-2025"
};

// App settings
export const appSettings = {
  // The name of the app displayed in the header
  appName: "Starstruck or Just Luck: Zodiac Experiment",
  
  // Copyright notice displayed in the footer
  copyrightNotice: "© 2025 Starstruck Research",
  
  // Enable fallback to localStorage if Firebase fails
  enableLocalStorageFallback: true,
  
  // Number of descriptions presented to users
  numDescriptionsToShow: 6
};

interface AppSettings {
  name: string;
  version: string;
  enableLocalStorageFallback: boolean;
  useLocalStorage: boolean;
  resultsCollection: string;
  storage: {
    type: 'localStorage' | 'firestore' | 'auto';
    forceType?: 'localStorage' | 'firestore';
  };
}

interface AuthSettings {
  password: string;
  requirePassword: boolean;
}

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

interface Config {
  app: AppSettings;
  auth: AuthSettings;
  firebase: FirebaseConfig;
}

const config: Config = {
  app: {
    name: 'Starstruck or Just Luck?',
    version: '1.0.0',
    enableLocalStorageFallback: isDevelopment(),
    useLocalStorage: isDevelopment(),
    resultsCollection: 'survey_results',
    storage: {
      // 'auto' will use localStorage in dev and firestore in prod
      // Can be overridden by forceType
      type: import.meta.env.VITE_STORAGE_TYPE as 'localStorage' | 'firestore' | 'auto' || 'auto',
      // Optional: Force a specific storage type regardless of environment
      forceType: import.meta.env.VITE_FORCE_STORAGE_TYPE as 'localStorage' | 'firestore' | undefined
    }
  },
  auth: {
    password: 'starstruck',
    requirePassword: true
  },
  firebase: firebaseConfig
};

// Development mode settings
if (isDevelopment()) {
  console.log('Development mode: Checking storage configuration');
  console.log('Storage configuration:', {
    type: config.app.storage.type,
    forceType: config.app.storage.forceType,
    env: {
      VITE_STORAGE_TYPE: import.meta.env.VITE_STORAGE_TYPE,
      VITE_FORCE_STORAGE_TYPE: import.meta.env.VITE_FORCE_STORAGE_TYPE
    }
  });

  // If forceType is set, use that regardless of environment
  if (config.app.storage.forceType) {
    console.log(`Development mode: Forcing storage type to ${config.app.storage.forceType}`);
    config.app.useLocalStorage = config.app.storage.forceType === 'localStorage';
  } else if (config.app.storage.type === 'auto') {
    console.log('Development mode: Using localStorage (auto mode)');
    config.app.useLocalStorage = true;
  } else {
    console.log(`Development mode: Using ${config.app.storage.type}`);
    config.app.useLocalStorage = config.app.storage.type === 'localStorage';
  }
  config.app.enableLocalStorageFallback = true;
} else {
  // Production mode: Require Firebase
  console.log('Production mode: Checking Firebase configuration');
  const isFirebaseConfigValid = () => {
    const isValid = config.firebase.apiKey && 
                    config.firebase.projectId && 
                    config.firebase.appId;
    console.log('Firebase config validation:', {
      hasApiKey: !!config.firebase.apiKey,
      hasProjectId: !!config.firebase.projectId,
      hasAppId: !!config.firebase.appId,
      isValid
    });
    return isValid;
  };

  if (!isFirebaseConfigValid()) {
    throw new Error('Firebase configuration is missing in production mode');
  }
  
  // If forceType is set, use that regardless of environment
  if (config.app.storage.forceType) {
    console.log(`Production mode: Forcing storage type to ${config.app.storage.forceType}`);
    config.app.useLocalStorage = config.app.storage.forceType === 'localStorage';
  } else if (config.app.storage.type === 'auto') {
    console.log('Production mode: Using Firestore (auto mode)');
    config.app.useLocalStorage = false;
  } else {
    console.log(`Production mode: Using ${config.app.storage.type}`);
    config.app.useLocalStorage = config.app.storage.type === 'localStorage';
  }
  config.app.enableLocalStorageFallback = false;
}

export default config; 